import type { ScrollMode } from "../scroll-mode.js";
import {
  FOLLOW_VOICE_MISSING,
  FOLLOW_VOICE_OFF,
  followerStatusLabel,
  type FollowerState,
} from "../speech.js";
import { formatDownloadSize } from "./manifest.js";
import type { ModelCommand, ModelStatus } from "./status.js";

/**
 * What is on screen about the model, in one place.
 *
 * Web ticket 15 wrote this beside the page's half of the download, for two
 * readers on that host: the block on the setup screen, where a first-time visitor
 * is deciding, and the line in the prompter's chrome, where somebody mid-session
 * wants to know why the prompter is not following. Two components rendering the
 * same seven phases would be two opinions about them, and the second one would
 * drift.
 *
 * It moved here when a third reader appeared on a different host (mobile-app
 * ticket 06). The argument is the one that moved the loader itself: the phases
 * are `@cuekards/core`'s, the phone runs the same machine through them, and a
 * panel on a phone that said something else about `downloading` would be the
 * drift this function was written to prevent — only now across two products
 * rather than two components.
 *
 * The sentences are deliberately unchanged in the move, including "kept on this
 * device", which reads correctly on a phone and on a tab and was written that way
 * before there was a phone. Nothing here names a browser.
 */

/** A button on the download panel. */
export interface ModelAction {
  label: string;
  command: ModelCommand;
}

export interface ModelOffer {
  /** What is going on, in a sentence. */
  says: string;
  /** The button that changes it, where there is one. */
  action?: ModelAction;
  /** The second button, where a state has two ways out. Shown on the panel and
   * not in the chrome: "Clear and start over" beside a prompter mid-take is a
   * control nobody reached for on purpose. */
  also?: ModelAction;
  /** How far along, 0 to 1, where there is a bar to draw. */
  progress?: number;
}

/**
 * Null in the two phases with nothing worth saying, which is the *silence* rule
 * rather than a missing case.
 *
 * `ready` is a working teleprompter working — the prompter's own chip already
 * says so from filming distance, and a second line saying it beside two boxes for
 * loading a script would be telling somebody their teleprompter is a
 * teleprompter. `checking` is the fraction of a second before the manifest comes
 * back, on every visit including warm ones, and a panel that flickered into
 * existence and out again on every load would be worse than one that waited.
 *
 * Returned as null rather than left to each caller, so the panel and the chip
 * cannot come to different conclusions about when to keep quiet.
 */
export function modelOffer(status: ModelStatus): ModelOffer | null {
  switch (status.phase) {
    case "checking":
    case "ready":
      return null;
    case "unavailable":
      return {
        says: `Speech-following is unavailable here: ${status.why} The prompter works in manual and constant-speed modes.`,
      };
    case "needed":
      return {
        // The size, before anything starts, which is the whole of story 19 — and
        // "once" and "on this device", because what the figure buys is every
        // later visit for nothing and no audio leaving the browser.
        says: `Follow your voice: a one-off ${formatDownloadSize(status.bytes)} download, kept on this device. Everything else works without it.`,
        action: { label: "Download", command: "start" },
      };
    case "downloading": {
      const progress = status.bytes === 0 ? 1 : status.received / status.bytes;
      return {
        says: `Downloading the speech model — ${Math.floor(progress * 100)}% of ${formatDownloadSize(status.bytes)}.`,
        action: { label: "Cancel", command: "cancel" },
        progress,
      };
    }
    case "preparing":
      return { says: "Starting the speech recogniser…" };
    case "failed":
      return {
        says: `The speech model could not be loaded: ${status.why}`,
        // Try again first, because a failed download usually has a whole file or
        // two behind it and trying again costs only what is left. Clearing is the
        // answer to the one that keeps failing.
        action: { label: "Try again", command: "start" },
        also: { label: "Clear and start over", command: "clear" },
      };
  }
}

/**
 * Why there is no recogniser yet, phrased for the middle of a sentence.
 *
 * The other side of {@link modelOffer}. That one is what a *panel* says, with a
 * button beside it; this is what the thing asking for a recogniser is told when
 * there is not one — and it ends up inside the prompter's own explanation, beside
 * a teleprompter that goes on working without one (goal G5).
 *
 * It says what would change it, because the panel that can change it is usually
 * on the same screen: "the model has not been downloaded" beside a button marked
 * Download is a complete thought, and "unavailable" beside the same button is
 * not.
 *
 * Written for the browser (web ticket 15) and moved here with the panel when the
 * phone needed the same seven answers (mobile-app ticket 06). Neither host says
 * anything the other would not: a compile step is the only thing they differ
 * about, and `preparing` is honest on both — the phone's is a turn of the loop
 * rather than nineteen megabytes of WebAssembly, and "still loading" describes
 * each.
 */
export function whyNoRecogniser(status: ModelStatus): string {
  switch (status.phase) {
    case "checking":
      return "the speech recogniser is starting up.";
    case "unavailable":
      return status.why;
    case "needed":
      return `the speech model has not been downloaded yet (${formatDownloadSize(status.bytes)}).`;
    case "downloading":
      return "the speech model is downloading.";
    case "preparing":
      return "the speech recogniser is still loading.";
    case "failed":
      return status.why;
    case "ready":
      // Unreachable in practice — a host in this phase has what it needs — but a
      // recogniser that threw with no message would be the one failure here with
      // nothing on screen to explain it.
      return "the speech recogniser could not be built.";
  }
}

/** The status line above the record button, and the one control it can carry. */
export interface FollowVoiceStatus {
  /** What follow-my-voice is doing, in a phrase. */
  says: string;
  /** The press that would change it, where there is one worth making from the
   * prompter: fetching a model that is not here, or fetching it again. */
  action?: ModelAction;
  /** The follower's own sentence, where it failed for a reason of its own. The
   * line has room for a phrase and this is a paragraph, so it goes in a title. */
  title?: string;
}

/**
 * The prompter's follow-my-voice line, from the follower, the model and the mode.
 *
 * {@link followerStatusLabel} on its own sees only the follower, and a follower
 * whose model has not been downloaded has nothing to report but an error — so
 * the line said "Unavailable" and nothing else, over a screen where the fix was
 * one press away. The model phase is what turns that into a reason and a button.
 *
 * In order, and the order is the whole of it:
 *
 * - **Listening wins.** A take or a rehearsal in progress is the thing on screen.
 * - **Then the model, while there is no recogniser.** `!follower.ready` as well as
 *   the phase, because a recogniser that was built is the better witness: the
 *   model's own phase lags it by a message.
 * - **Then the mode.** A recogniser ready over a prompter in `constant` is not
 *   following anybody, and "ready" there would be a claim the prompter does not
 *   keep. It matters more now there is a Download button on this line: pressed
 *   from `constant`, the download would otherwise finish on "ready" over a script
 *   that never moves — which is why the press also turns the mode on.
 * - **Then the follower's own word**, with its sentence as the title when it
 *   failed for a reason the model knows nothing about.
 */
export function followVoiceStatus(input: {
  follower: FollowerState;
  model: ModelStatus;
  mode: ScrollMode;
}): FollowVoiceStatus {
  const { follower, model, mode } = input;
  if (follower.listening) return { says: followerStatusLabel(follower) };
  if (!follower.ready && model.phase !== "ready") return modelLine(model);
  if (mode !== "speech") return { says: FOLLOW_VOICE_OFF };
  return {
    says: followerStatusLabel(follower),
    ...(follower.error === undefined ? {} : { title: follower.error }),
  };
}

/** The line for each phase that has no recogniser in it yet. */
function modelLine(
  model: Exclude<ModelStatus, { phase: "ready" }>,
): FollowVoiceStatus {
  switch (model.phase) {
    case "needed":
      return {
        says: FOLLOW_VOICE_MISSING,
        // The size on the button, because the button is the consent: the
        // green room asks in a dialog, and here the label has to carry what the
        // dialog would have said. What is still absent, not the manifest total.
        action: {
          label: `Download voice recogniser · ${formatDownloadSize(model.bytes)}`,
          command: "start",
        },
      };
    case "downloading": {
      const progress = model.bytes === 0 ? 1 : model.received / model.bytes;
      return {
        says: `Downloading voice recogniser · ${Math.floor(progress * 100)}%`,
      };
    }
    case "checking":
    case "preparing":
      return { says: "Starting voice recogniser…" };
    case "failed":
      return {
        says: "Voice recogniser download failed",
        action: { label: "Try again", command: "start" },
        title: model.why,
      };
    case "unavailable":
      // Not "missing": there is nothing to download, this browser cannot run it.
      return { says: "Follow my voice unavailable here", title: model.why };
  }
}
